import React, { useRef, useState } from "react";
import axios from "axios";
import { Download, Upload, Loader2, CheckCircle2, AlertCircle, Copy, FileSpreadsheet } from "lucide-react";
import { LegacyImport } from "./LegacyImport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  ok: { label: "Eklenecek", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  duplicate: { label: "Zaten kayıtlı", cls: "bg-amber-50 text-amber-700", icon: Copy },
  error: { label: "Hatalı", cls: "bg-red-50 text-red-700", icon: AlertCircle },
};

const PreviewTable = ({ rows }) => (
  <div className="overflow-x-auto rounded-xl border border-[#F0DDE0]" data-testid="import-preview-table">
    <table className="w-full text-sm">
      <thead className="bg-[#FBF5F0] text-[#7C6E70] text-left">
        <tr>{["Satır", "Firma", "Kategori", "Şehir / İlçe", "Telefon", "Durum"].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const st = STATUS[r.status];
          return (
            <tr key={r.row} className="border-t border-[#F6EAEC]" data-testid={`import-row-${r.row}`}>
              <td className="px-3 py-2 text-[#7C6E70]">{r.row}</td>
              <td className="px-3 py-2 font-medium text-[#2A2224]">{r.name || "—"}</td>
              <td className="px-3 py-2">{r.category || "—"}</td>
              <td className="px-3 py-2 whitespace-nowrap">{[r.city, r.district].filter(Boolean).join(" / ") || "—"}</td>
              <td className="px-3 py-2 whitespace-nowrap">{r.phone || "—"}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}><st.icon className="w-3 h-3" />{st.label}</span>
                {r.errors.length > 0 && <div className="text-xs text-[#9E2A3B] mt-1">{r.errors.join(", ")}</div>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const BulkFirmImport = ({ headers, toast, onDone }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const r = await axios.get(`${API}/admin/firms/import-template`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "dugunsenin-firma-sablonu.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast({ title: "Hata", description: "Şablon indirilemedi." }); }
    setBusy("");
  };

  const send = async (f, commit) => {
    setBusy(commit ? "commit" : "preview");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/admin/firms/import`, fd, { headers, params: { commit } });
      if (commit) {
        toast({ title: "Firmalar yüklendi", description: `${r.data.created} firma "Beklemede" olarak eklendi. Onaylayınca sitede görünecek.` });
        setFile(null); setResult(null);
        onDone();
      } else setResult(r.data);
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Dosya işlenemedi." });
      if (!commit) { setFile(null); setResult(null); }
    }
    setBusy("");
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f); setResult(null); send(f, false);
  };

  const s = result?.summary;
  return (
    <div className="space-y-6" data-testid="bulk-import-section">
      <LegacyImport headers={headers} toast={toast} />
      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
        <h2 className="font-display text-2xl text-[#2A2224] flex items-center gap-2"><FileSpreadsheet className="w-6 h-6 text-[#9E2A3B]" /> Excel ile Toplu Firma Yükleme</h2>
        <p className="text-sm text-[#7C6E70] mt-2 max-w-2xl">1) Şablonu indirin, 2) firmaları doldurun, 3) dosyayı yükleyip önizlemeyi kontrol edin, 4) onaylayın. Firmalar <b>Beklemede</b> olarak eklenir; İşletmeler listesinden onayladığınızda sitede görünür.</p>
        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={downloadTemplate} disabled={!!busy} data-testid="import-download-template" className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBEEF0] transition-colors disabled:opacity-50">
            {busy === "template" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Excel Şablonunu İndir
          </button>
          <button onClick={() => inputRef.current?.click()} disabled={!!busy} data-testid="import-upload-btn" className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-50">
            {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Doldurulmuş Excel'i Yükle
          </button>
          <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={pick} data-testid="import-file-input" />
        </div>
        {file && <p className="text-xs text-[#7C6E70] mt-3" data-testid="import-file-name">Dosya: {file.name}</p>}
      </div>

      {result && (
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 space-y-4" data-testid="import-preview">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700" data-testid="import-count-ok">{s.ok} eklenecek</span>
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700" data-testid="import-count-duplicate">{s.duplicate} zaten kayıtlı</span>
            <span className="px-3 py-1 rounded-full bg-red-50 text-red-700" data-testid="import-count-error">{s.error} hatalı</span>
            <button onClick={() => send(file, true)} disabled={!s.ok || !!busy} data-testid="import-commit-btn" className="ml-auto inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium disabled:opacity-50">
              {busy === "commit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {s.ok} Firmayı Ekle
            </button>
          </div>
          {s.error > 0 && <p className="text-xs text-[#9E2A3B]">Hatalı ve zaten kayıtlı satırlar eklenmez. Excel'i düzeltip tekrar yükleyebilirsiniz.</p>}
          {result.rows.length ? <PreviewTable rows={result.rows} /> : <p className="text-sm text-[#7C6E70]">Dosyada firma satırı bulunamadı.</p>}
        </div>
      )}
    </div>
  );
};
